import { Link } from 'react-router-dom'
import { FaFacebook, FaInstagram, FaTiktok, FaWhatsapp } from 'react-icons/fa'
import { useSite } from '../../context/SiteContext'

// ─── Column data ──────────────────────────────────────────────────────────────
const QUICK_LINKS = [
  { to: '/',         label: 'Home'     },
  { to: '/packages', label: 'Packages' },
  { to: '/services', label: 'Services' },
  { to: '/gallery',  label: 'Gallery'  },
]

const MORE_LINKS = [
  { to: '/about',   label: 'About Us' },
  { to: '/contact', label: 'Contact'  },
]

// ─── Sub-components ───────────────────────────────────────────────────────────
function FooterLink({ to, children }) {
  return (
    <li>
      <Link
        to={to}
        className="flex items-center gap-2 text-white/70 hover:text-secondary transition-colors duration-200 text-sm font-body"
      >
        <span className="text-primary text-xs">›</span>
        {children}
      </Link>
    </li>
  )
}

function SocialButton({ href, label, children }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      aria-label={label}
      className="w-11 h-11 rounded-full bg-white/10 hover:bg-primary flex items-center justify-center transition-all duration-200 hover:scale-110 text-white hover:text-white"
    >
      {children}
    </a>
  )
}

// ─── Component ────────────────────────────────────────────────────────────────
export default function Footer() {
  const { siteOptions } = useSite()
  const contact = siteOptions?.contact || {}
  const social  = siteOptions?.social  || {}

  return (
    <footer
      style={{ background: 'linear-gradient(135deg, #1A0A2E 0%, #3D1A7A 60%, #7C3AFF 100%)' }}
    >
      {/* ── Main grid ─────────────────────────────────────────────────────── */}
      <div className="container-custom py-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">

        {/* Column 1 — Brand ───────────────────────────────────────────────── */}
        <div className="sm:col-span-2 lg:col-span-1">
          {/* Mouse image / logo */}
          <Link to="/" className="inline-flex items-center gap-3 mb-5">
            <img
              src="/assets/mouse-suit.png"
              alt="Farafeero mascot"
              width={56}
              height={56}
              className="rounded-2xl"
              onError={(e) => {
                e.currentTarget.style.display = 'none'
                e.currentTarget.nextElementSibling.style.display = 'flex'
              }}
            />
            {/* Fallback emoji shown when image missing */}
            <div className="hidden w-14 h-14 rounded-2xl bg-primary/20 items-center justify-center text-3xl">
              🐭
            </div>
            <span className="font-display text-xl text-white leading-none">
              Farafeero
              <span className="text-primary"> World</span>
            </span>
          </Link>

          {/* Tagline */}
          <p className="text-white/70 text-sm font-body leading-relaxed mb-5 max-w-xs">
            Making every birthday magical ✨<br />
            Premium children's party experiences crafted with love in Cairo, Egypt.
          </p>

          {/* Pink heart divider */}
          <div className="flex items-center gap-3 mb-5">
            <div className="h-px bg-white/20 flex-1" />
            <span className="text-primary text-lg">♥</span>
            <div className="h-px bg-white/20 flex-1" />
          </div>

          {/* Social icons */}
          <div className="flex items-center gap-2.5">
            {social.facebook && (
              <SocialButton href={social.facebook} label="Facebook">
                <FaFacebook size={18} />
              </SocialButton>
            )}
            {social.instagram && (
              <SocialButton href={social.instagram} label="Instagram">
                <FaInstagram size={18} />
              </SocialButton>
            )}
            {social.tiktok && (
              <SocialButton href={social.tiktok} label="TikTok">
                <FaTiktok size={18} />
              </SocialButton>
            )}
            {contact.whatsapp && (
              <SocialButton
                href={`https://wa.me/${contact.whatsapp}`}
                label="WhatsApp"
              >
                <FaWhatsapp size={18} />
              </SocialButton>
            )}
            {/* Fallback social icons when WP options not loaded yet */}
            {!social.facebook && !social.instagram && (
              <>
                <SocialButton href="#" label="Facebook">
                  <FaFacebook size={18} />
                </SocialButton>
                <SocialButton href="#" label="Instagram">
                  <FaInstagram size={18} />
                </SocialButton>
                <SocialButton href="#" label="TikTok">
                  <FaTiktok size={18} />
                </SocialButton>
              </>
            )}
          </div>
        </div>

        {/* Column 2 — Quick Links ─────────────────────────────────────────── */}
        <div>
          <h3 className="font-display text-white text-lg mb-6">Quick Links</h3>
          <ul className="space-y-3">
            {QUICK_LINKS.map(({ to, label }) => (
              <FooterLink key={to} to={to}>{label}</FooterLink>
            ))}
          </ul>
        </div>

        {/* Column 3 — Contact ─────────────────────────────────────────────── */}
        <div>
          <h3 className="font-display text-white text-lg mb-6">Get In Touch</h3>
          <ul className="space-y-3">
            {MORE_LINKS.map(({ to, label }) => (
              <FooterLink key={to} to={to}>{label}</FooterLink>
            ))}
            {(contact.whatsapp || true) && (
              <li>
                <a
                  href={`https://wa.me/${contact.whatsapp || '201000000000'}`}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-2 text-white/70 hover:text-secondary transition-colors duration-200 text-sm font-body"
                >
                  <span className="text-primary text-xs">›</span>
                  WhatsApp Us
                </a>
              </li>
            )}
            {contact.email && (
              <li>
                <a
                  href={`mailto:${contact.email}`}
                  className="flex items-center gap-2 text-white/70 hover:text-secondary transition-colors duration-200 text-sm font-body break-all"
                >
                  <span className="text-primary text-xs">›</span>
                  {contact.email}
                </a>
              </li>
            )}
            {contact.phone && (
              <li>
                <a
                  href={`tel:${contact.phone}`}
                  className="flex items-center gap-2 text-white/70 hover:text-secondary transition-colors duration-200 text-sm font-body"
                >
                  <span className="text-primary text-xs">›</span>
                  {contact.phone}
                </a>
              </li>
            )}
          </ul>
        </div>

        {/* Column 4 — Follow Us ───────────────────────────────────────────── */}
        <div>
          <h3 className="font-display text-white text-lg mb-6">Follow Us</h3>
          <p className="text-white/60 text-sm font-body mb-6 leading-relaxed">
            See the magic in action — follow us for party inspo, behind-the-scenes fun, and happy families!
          </p>

          <div className="flex flex-wrap gap-4">
            {[
              { Icon: FaFacebook,  label: 'Facebook',  href: social.facebook  || '#', color: 'hover:bg-blue-600'  },
              { Icon: FaInstagram, label: 'Instagram', href: social.instagram || '#', color: 'hover:bg-pink-600'  },
              { Icon: FaTiktok,    label: 'TikTok',    href: social.tiktok    || '#', color: 'hover:bg-gray-900'  },
              { Icon: FaWhatsapp,  label: 'WhatsApp',  href: `https://wa.me/${contact.whatsapp || '201000000000'}`, color: 'hover:bg-green-600' },
            ].map(({ Icon, label, href, color }) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noreferrer"
                aria-label={label}
                className={[
                  'flex items-center gap-2 px-4 py-2 rounded-full',
                  'bg-white/10 text-white text-sm font-body',
                  'hover:text-white hover:scale-105',
                  'transition-all duration-200',
                  color,
                ].join(' ')}
              >
                <Icon size={18} />
                {label}
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* ── Copyright bar ─────────────────────────────────────────────────── */}
      <div className="border-t border-white/10">
        <div className="container-custom py-5 flex flex-col sm:flex-row items-center justify-between gap-2">
          <p className="text-white/50 text-xs font-body text-center">
            © {new Date().getFullYear()} Farafeero World. All Rights Reserved.
          </p>
          <p className="text-white/40 text-xs font-body">
            Made with ♥ in Cairo, Egypt
          </p>
        </div>
      </div>
    </footer>
  )
}
